<template>
  <div v-if="isAdmin || isStaff" class="companies-container">
    <!-- Header -->
    <div class="header">
      <h1>
        {{ 'activities.clients' | translate }} 
        (<span v-if="!initialLoading">{{ countAll || 0 }}</span>
        <span v-else class="skeleton-count-inline skeleton-shimmer"></span>)
      </h1>
      <div class="header-controls">
        <div class="search-container">
          <input type="text" v-model="searchQuery" @input="onSearchInput" :placeholder="'activities.search' | translate"
            class="search-input">
          <div class="search-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="7" stroke="#979797" stroke-width="2" />
              <path d="M20 20L17 17" stroke="#979797" stroke-width="2" stroke-linecap="round" />
            </svg>
          </div>
          <button v-if="searchQuery" @click="clearSearch" class="search-clear">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="#979797" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </div>
        <div class="header-controls-right">
          <div class="header-controls-date">{{ currentDate }}</div>

          <v-btn :disabled="isStaff" class="add-company-btn ml-4" medium @click="modals.create = true">
            <span class="plus-icon">+</span>
            <span>{{ 'activities.add_client' | translate }}</span>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Skeleton Loader for Loading State -->
    <div v-if="loading">
      <!-- Skeleton Table Header -->
      <div class="table-header">
        <div class="table-row">
          <div class="table-cell header-cell company-name">
            <div class="skeleton-header skeleton-shimmer"></div>
          </div>
          <div class="table-cell header-cell farm-status">
            <div class="skeleton-header skeleton-shimmer"></div>
          </div>
          <div class="table-cell header-cell farms-count">
            <div class="skeleton-header skeleton-shimmer"></div>
          </div>
          <div class="table-cell header-cell animals-count">
            <div class="skeleton-header skeleton-shimmer"></div>
          </div>
          <div class="table-cell header-cell mixings-ratio">
            <div class="skeleton-header skeleton-shimmer"></div>
          </div>
          <div class="table-cell header-cell unique-mixers">
            <div class="skeleton-header skeleton-shimmer"></div>
          </div>
          <div class="table-cell header-cell actions">
            <div class="skeleton-header skeleton-shimmer"></div>
          </div>
          <div class="table-cell header-cell menu"></div>
        </div>
      </div>

      <!-- Skeleton Table Body -->
      <div class="table-body">
        <div v-for="n in 8" :key="`skeleton-${n}`" class="table-row company-row skeleton-row">
          <div class="table-cell company-name">
            <div class="skeleton-company-name skeleton-shimmer"></div>
          </div>
          <div class="table-cell status">
            <div class="skeleton-status skeleton-shimmer"></div>
          </div>
          <div class="table-cell farms-count">
            <div class="skeleton-count skeleton-shimmer"></div>
          </div>
          <div class="table-cell animals-count">
            <div class="skeleton-badge skeleton-shimmer"></div>
          </div>
          <div class="table-cell mixings-ratio">
            <div class="skeleton-ratio skeleton-shimmer"></div>
          </div>
          <div class="table-cell unique-mixers">
            <div class="skeleton-count skeleton-shimmer"></div>
          </div>
          <div class="table-cell actions">
            <div class="skeleton-icon skeleton-shimmer"></div>
          </div>
          <div class="table-cell menu">
            <div class="skeleton-menu skeleton-shimmer"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state when no clients -->
    <div v-else-if="!loading && filteredCompanies.length === 0" class="loading-container">
      <p>{{ 'activities.no_clients' | translate }}</p>
    </div>

    <!-- Actual Content -->
    <div v-else>
      <!-- Table Header -->
      <div class="table-header">
        <div class="table-row">
          <div class="table-cell header-cell company-name sortable"
            :class="{ 'active-sort': sortField === 'display_name' }" @click="sortBy('display_name')">
            <span>{{ 'activities.client' | translate }}</span>
            <span class="sort-indicator" :class="{
              'active': sortField === 'display_name',
              'asc': sortField === 'display_name' && sortDirection === 'asc',
              'desc': sortField === 'display_name' && sortDirection === 'desc'
            }">
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.0781 1.16064L6.0036 6.07837L1.07932 1.00161" stroke="currentColor" stroke-width="2" />
              </svg>
            </span>
          </div>

          <div class="table-cell header-cell farm-status sortable"
            :class="{ 'active-sort': sortField === 'latest_status' }" @click="sortBy('latest_status')">
            <span>{{ 'activities.status' | translate }}</span>
            <span class="sort-indicator" :class="{
              'active': sortField === 'latest_status',
              'asc': sortField === 'latest_status' && sortDirection === 'asc',
              'desc': sortField === 'latest_status' && sortDirection === 'desc'
            }">
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.0781 1.16064L6.0036 6.07837L1.07932 1.00161" stroke="currentColor" stroke-width="2" />
              </svg>
            </span>
          </div>
          <div class="table-cell header-cell farms-count">{{ 'activities.farms' | translate }}</div>
          <div class="table-cell header-cell animals-count">{{ 'activities.number_of_days_in_status' | translate }}</div>
          <div class="table-cell header-cell mixings-ratio">{{ 'activities.number_of_downloads' | translate }}</div>
          <div class="table-cell header-cell unique-mixers">{{ 'activities.unique_mixers' | translate }}</div>
          <div class="table-cell header-cell actions">{{ 'activities.comments' | translate }}</div>
          <div class="table-cell header-cell menu"></div>
        </div>
      </div>

      <!-- Table Body -->
      <div class="table-body">
        <div v-for="company in filteredCompanies" :key="company.id">
          <!-- Main Company Row -->
          <div class="table-row company-row" :class="[
    getRowClass(company), 
    { 'deactivated': !company.is_active }
  ]">
            <div class="table-cell company-name">
              <div class="company-info">
                <div @click="openClient(company.id, company.display_name)" class="company-name-text">
                  {{ company.display_name || company.name }}
                </div>
              </div>
            </div>

            <div class="table-cell status">
              {{ getStatusText(company.latest_status) }}
            </div>

            <div class="table-cell farms-count">
              {{ company.farm_count }}
            </div>

            <div class="table-cell animals-count">
              <span @click="showCompanyActivities(company)" class="farms-badge">{{ company.consecutive_days }}</span>
            </div>

            <div class="table-cell mixings-ratio">
              <span class="ratio" :class="getRowClass(company)">
                {{ company.total_done }}/{{ company.total_planned }}
              </span>
            </div>
            <div class="table-cell unique-mixers">
              {{ company.unique_mixer_count }}
            </div>

            <div class="table-cell actions" @click.stop>
              <button v-if="hasComments(company)" class="icon-btn comment-btn" @click.stop="showComment(company)"
                :title="'activities.view_comments' | translate">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 3C14.7956 3 16.1933 3.00033 17.2959 3.45703C18.7661 4.06599 19.934 5.23393 20.543 6.7041C20.9997 7.80672 21 9.2044 21 12C21 14.7956 20.9997 16.1933 20.543 17.2959C19.934 18.7661 18.7661 19.934 17.2959 20.543C16.1933 20.9997 14.7956 21 12 21H9C6.17157 21 4.75759 20.9998 3.87891 20.1211C3.00023 19.2424 3 17.8284 3 15V12C3 9.2044 3.00033 7.80672 3.45703 6.7041C4.06599 5.23393 5.23393 4.06599 6.7041 3.45703C7.80672 3.00033 9.2044 3 12 3ZM9 13C8.44772 13 8 13.4477 8 14C8.00018 14.5521 8.44783 15 9 15H12C12.5522 15 12.9998 14.5521 13 14C13 13.4477 12.5523 13 12 13H9ZM9 9C8.44772 9 8 9.44772 8 10C8.00018 10.5521 8.44783 11 9 11H15C15.5522 11 15.9998 10.5521 16 10C16 9.44772 15.5523 9 15 9H9Z"
                    fill="#5EBE40" />
                </svg>
              </button>

              <button v-else class="icon-btn add-comment-btn" @click.stop="openAddCommentForm(company)"
                :title="'activities.add_comments' | translate">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 4V4C8.22876 4 6.34315 4 5.17157 5.17157C4 6.34315 4 8.22876 4 12V18C4 18.9428 4 19.4142 4.29289 19.7071C4.58579 20 5.05719 20 6 20H12C15.7712 20 17.6569 20 18.8284 18.8284C20 17.6569 20 15.7712 20 12V12"
                    stroke="#5ebe40" stroke-width="2" />
                  <path d="M9 10L15 10" stroke="#5ebe40" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path d="M9 14H12" stroke="#5ebe40" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M19 8L19 2M16 5H22" stroke="#5ebe40" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </button>
            </div>

            <div class="table-cell menu" @click.stop>
              <settings-client-dropdown v-if="!isStaff" :id="company.id" @edit="editCompany(company)" @change="changePassword(company)">
              </settings-client-dropdown>
              <v-btn v-if="company.farm_count !== 0" @click="toggleCompanyExpansion(company.id)" icon
                :loading="loadingFarms[company.id]" :disabled="loadingFarms[company.id]">
                <v-chevron v-if="!loadingFarms[company.id]" :value="expandedCompanies.includes(company.id)" />
              </v-btn>
            </div>
          </div>

         <!-- Expanded Farms Rows -->
  <div v-if="expandedCompanies.includes(company.id) && company.farms">
    <div class="table-row farms-search-row">
      <div class="table-cell" colspan="8">
        <div class="farms-search-container">
          <div class="farms-search-input-wrapper">
            <input type="text" :value="farmsSearch[company.id] || ''"
              @input="onFarmsSearchInput(company.id, $event.target.value)"
              :placeholder="$t('activities.search_farms')" class="farms-search-input">
            <div class="farms-search-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="7" stroke="#979797" stroke-width="2" />
                <path d="M20 20L17 17" stroke="#979797" stroke-width="2" stroke-linecap="round" />
              </svg>
            </div>
            <button v-if="farmsSearch[company.id]" @click="clearFarmsSearch(company.id)"
              class="farms-search-clear">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="#979797" stroke-width="2" stroke-linecap="round" />
              </svg>
            </button>
          </div>
          <div class="farms-search-stats" v-if="company.farms">
            {{ company.farms.length }}
            {{ farmsSearch[company.id] ? $t('activities.found_farms') : $t('activities.farms_shown') }}
            <span v-if="farmsPagination[company.id]">
              {{ $t('activities.of') }} {{ farmsPagination[company.id].totalCount }}
            </span>
          </div>
        </div>
      </div>
    </div>
    
    <div v-if="company.farms && company.farms.length > 0">
      <div v-for="farm in company.farms" :key="`${company.id}-${farm.id}`" class="table-row farm-row">
        <div class="table-cell company-name">
          <div class="farm-info">
            <!-- <div class="farm-name-text">{{ farm.name }}</div> -->
          </div>
        </div>

        <div class="table-cell farm-status">
          <div class="farm-row-indicator" :class="getFarmClass(farm)"></div>
          <div class="farm-status">{{ getStatusText(farm.latest_status) }}</div>
        </div>

        <div class="table-cell farms-count" @click="goToFarm(company, farm)">
          {{ formatFarmName(farm.name) }}
        </div>

        <div class="table-cell animals-count">
          <span @click="showFarmActivities(company, farm)" class="farms-badge">{{ farm.consecutive_days }}</span>
        </div>

        <div class="table-cell mixings-ratio">
          <span class="ratio" :class="getFarmClass(farm)">
            {{ farm.total_done }}/{{ farm.total_planned }}
          </span>
        </div>
        <div class="table-cell unique-mixers">
          {{ farm.unique_mixer_count }}
        </div>

        <div class="table-cell actions" @click.stop>
          <button v-if="hasComments(farm)" class="icon-btn comment-btn" @click.stop="showComment(company, farm)"
            :title="'activities.view_comments' | translate">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 3C14.7956 3 16.1933 3.00033 17.2959 3.45703C18.7661 4.06599 19.934 5.23393 20.543 6.7041C20.9997 7.80672 21 9.2044 21 12C21 14.7956 20.9997 16.1933 20.543 17.2959C19.934 18.7661 18.7661 19.934 17.2959 20.543C16.1933 20.9997 14.7956 21 12 21H9C6.17157 21 4.75759 20.9998 3.87891 20.1211C3.00023 19.2424 3 17.8284 3 15V12C3 9.2044 3.00033 7.80672 3.45703 6.7041C4.06599 5.23393 5.23393 4.06599 6.7041 3.45703C7.80672 3.00033 9.2044 3 12 3ZM9 13C8.44772 13 8 13.4477 8 14C8.00018 14.5521 8.44783 15 9 15H12C12.5522 15 12.9998 14.5521 13 14C13 13.4477 12.5523 13 12 13H9ZM9 9C8.44772 9 8 9.44772 8 10C8.00018 10.5521 8.44783 11 9 11H15C15.5522 11 15.9998 10.5521 16 10C16 9.44772 15.5523 9 15 9H9Z"
                fill="#5EBE40" />
            </svg>
          </button>

          <button v-else class="icon-btn add-comment-btn" @click.stop="openAddCommentForm(company, farm)"
            :title="'activities.add_comments' | translate">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 4V4C8.22876 4 6.34315 4 5.17157 5.17157C4 6.34315 4 8.22876 4 12V18C4 18.9428 4 19.4142 4.29289 19.7071C4.58579 20 5.05719 20 6 20H12C15.7712 20 17.6569 20 18.8284 18.8284C20 17.6569 20 15.7712 20 12V12"
                stroke="#5ebe40" stroke-width="2" />
              <path d="M9 10L15 10" stroke="#5ebe40" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
              <path d="M9 14H12" stroke="#5ebe40" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
              <path d="M19 8L19 2M16 5H22" stroke="#5ebe40" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </button>
        </div>

        <div class="table-cell menu" @click.stop>
        </div>
      </div>
    </div>
    
    <div v-if="company.farms && company.farms.length === 0 && farmsPagination[company.id] && !farmsPagination[company.id].loading" class="table-row no-farms-row">
      <div class="table-cell" colspan="8">
        <div class="no-farms-message">
          <span v-if="farmsSearch[company.id]">
            {{ $t('activities.no_farms_found') }}: "{{ farmsSearch[company.id] }}"
          </span>
          <span v-else>
            {{ $t('activities.no_farms') }}
          </span>
        </div>
      </div>
    </div>
    
    <div v-if="farmsPagination[company.id] && farmsPagination[company.id].hasMore" class="table-row load-more-row">
      <div class="table-cell" colspan="8">
        <button class="load-more-button" :disabled="farmsPagination[company.id].loading"
          @click="loadMoreFarms(company.id)">
          <span v-if="!farmsPagination[company.id].loading">
            {{ 'base.loadmore' | translate }}
            ({{ company.farms.length }}/{{ farmsPagination[company.id].totalCount }})
          </span>
          <span v-else>
            {{ 'base.loading' | translate }}...
          </span>
        </button>
      </div>
    </div>
  </div>
        </div>
      </div>

      <!-- Pagination -->
      <ui-pagination v-if="showPagination && !loading" v-model="page" :page-count="pageCount"
        @page-change="pageChangeHandler" />
    </div>

    <!-- Modals -->
    <v-dialog v-model="modals.create" max-width="600">
      <create-modal @close="modals.create = false" @update="loadData" />
    </v-dialog>

    <v-dialog max-width="600" :value="selected !== null" @input="selected = null">
      <edit-modal :client="selected" @update="loadData" @close="selected = null" />
    </v-dialog>

    <!-- API Comment Modals -->
    <comment-modal :show="commentModal.show" :farm="selectedFarm" :client="selectedCommentClient"
      :show-edit-button="canEditComments" @close="closeCommentModal" @add-comment="handleAddCommentFromModal" @comment-deleted="handleCommentDeleted" />

    <comment-form-modal :show="commentFormModal.show" :client="selectedCommentClient" :farm="selectedFarm"
      :default-author="currentUserName" :existing-comment="existingCommentForEdit" @close="closeCommentFormModal"
      @create="handleCreateComment" />

    <!-- Monthly Activities Modal -->
    <monthly-activities-modal :show="showMonthlyActivities" :client="selectedClient" @close="closeMonthlyActivities" />
    <monthly-farm-activities-modal :show="showFarmMonthlyActivities" :client="selectedClient" :farm="selectedFarm"
      @close="closeMonthlyFarmActivities" />

    <!-- Settings Client Modals -->
    <v-dialog max-width="620" v-model="modals.showSettingsClient">
      <settings-client-modal v-if="selectedClient" :item="selectedClient"
        @close="modals.showSettingsClient = false; selectedClient = null; loadData()">
      </settings-client-modal>
    </v-dialog>

    <v-dialog max-width="320" v-model="modals.showChangePasswordClient">
      <change-password-client-modal v-if="selectedClient" :id="selectedClient.id"
        @close="modals.showChangePasswordClient = false; selectedClient = null; loadData()">
      </change-password-client-modal>
    </v-dialog>
  </div>
  <div v-else>Access denied!</div>
</template>

<script>
import api from '@/api'
import CreateModal from './modals/CreateModal'
import EditModal from './modals/EditModal'
import UiPagination from '@/components/ui/ui-pagination/UiPagination'
import paginationMixin from '@/mixins/pagination.mixin'
import MonthlyActivitiesModal from './modals/MonthlyActivitiesModal.vue'
import MonthlyFarmActivitiesModal from './modals/MonthlyFarmActivitiesModal.vue'
import CommentModal from './modals/CommentModal.vue'
import CommentFormModal from './modals/CommentFormModal.vue'
import SettingsClientDropdown from '../dropdowns/SettingsClientDropdown.vue'
import ChangePasswordClientModal from '../modals/ChangePasswordClientModal'
import SettingsClientModal from '../modals/SettingsClientModal'
export default {
  name: 'CompaniesTable',

  mixins: [paginationMixin],

  components: {
    CreateModal,
    EditModal,
    UiPagination,
    ChangePasswordClientModal,
    SettingsClientModal,
    SettingsClientDropdown,
    MonthlyActivitiesModal,
    MonthlyFarmActivitiesModal,
    CommentModal,
    CommentFormModal
  },

  data () {
    return {
      searchQuery: '',
      userType: null,
      searchTimeout: null,
      sortField: 'latest_status',
      sortDirection: 'desc',
      modals: {
        create: false,
        showSettingsClient: false,
        showChangePasswordClient: false
      },
      selected: null,
      expandedCompanies: [],
      loadingFarms: {},

      commentModal: {
        show: false
      },
      commentFormModal: {
        show: false
      },
      initialLoading: true,

      selectedClient: null,
      selectedFarm: null,
      selectedCommentClient: null,
      existingCommentForEdit: null,
      farmsPagination: {},
      farmsSearch: {},
      farmsSearchTimeout: {},
      currentUserName: 'Admin',
      showMonthlyActivities: false,
      showFarmMonthlyActivities: false
    }
  },

  computed: {
    canEditComments () {
      return this.userType !== 'employee' && this.userType !== 'staff'
    },
    currentDate () {
      const monthKeys = [
        'january', 'february', 'march', 'april', 'may', 'june',
        'july', 'august', 'september', 'october', 'november', 'december'
      ]

      const months = monthKeys.map(key => this.$t(`date.months.${key}`))


      const now = new Date()
      const day = now.getDate()
      const month = months[now.getMonth()]
      const year = now.getFullYear()

      return `${day} ${month} ${year}`
    },
    filteredCompanies () {
      return this.items.map(company => ({
        ...company,
        farms: company.farms || [],
        rowType: this.getRowType(company.latest_status, company.is_active)
      }))
    },

    isConsultant () {
      return this.userType === 'consultant'
    },

    groupId () {
      return this.$route.params.ac_client_id
    }
  },
  watch: {
    countAll (newVal) {
      if (newVal !== null) {
        this.initialLoading = false
      }
    }
  },
  async created () {
    try {
      const { data } = await api.account.get()
      this.userType = data.user_type
      this.currentUserName = data.full_name || data.first_name || 'Client'
    } catch (error) {
      console.error('Failed to fetch user type:', error)
      this.userType = null
      this.currentUserName = 'Client'
    }
  },

  async mounted () {
    this.loading = true
    await this.setupPagination(
      this.fetchCompanies,
      {},
      `companies_${this.groupId}`
    )
  },

  beforeDestroy () {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },

  methods: {
    formatFarmName (name) {
      return name.replace(/([(),])/g, ' $1 ')
    },
    async openClient (id, name) {
      let clients = JSON.parse(localStorage.getItem('activities_clients') || '{}')
      clients[id] = name
      localStorage.setItem('activities_clients', JSON.stringify(clients))
      this.$store.dispatch('setClientId', id)
      this.$router.push({
        path: `/app/activities/${this.groupId}/clients/${id}/companies`,
        query: { page: 1 }
      })
    },
    async fetchCompanies (params) {
      const { offset } = params

      const payload = {
        group: this.groupId,
        sorted_by: '-latest_status',
        ...(offset && { offset })
      }

      if (this.groupId === 'null') {
        payload.group = -1
      }

      if (this.searchQuery && this.searchQuery.trim()) {
        payload.display_name = this.searchQuery.trim()
      }

      if (this.sortField) {
        const sortPrefix = this.sortDirection === 'desc' ? '-' : ''
        payload.sorted_by = `${sortPrefix}${this.sortField}`
      } else {
        payload.sorted_by = '-latest_status'
      }

      const res = await api.dashboard.clients.listV2(payload)

      const responseData = res.data || []
      const responseCount = res.count || responseData.length

      return {
        data: responseData,
        count: responseCount
      }
    },
    async goToFarm (company, farm) {
      try {
        const clientId = company.id
        const farmId = farm.id

        this.$store.dispatch('setClientId', clientId)

        const { data } = await api.farms.getById(farmId)
        const companyId = data.company.id

        if (!companyId) {
          throw new Error('Company ID not found in farm data')
        }

        this.$router.push(`/app/activities/${this.groupId}/clients/${clientId}/companies/${companyId}/farms/${farmId}`)
      } catch (error) {
        console.error('Error navigating to farm:', error)
      }
    },
    getStatusText (status) {
      const statusMap = {
        3: this.$t('activities.status_active'),
        2: this.$t('activities.status_warning'),
        1: this.$t('activities.status_not_working'),
        0: this.$t('activities.status_registration')
      }
      return statusMap[status] || 'Unknown'
    },

    getRowType (status, isActive) {
      switch (status) {
        case 2: return 'warning'
        case 1: return 'error'
        case 3: return 'success'
        default: return 'default'
      }
    },
    clearSearch () {
      this.searchQuery = ''
      this.page = 1
      this.loadData()
    },
    showCompanyActivities (company) {
      this.selectedClient = company
      this.showMonthlyActivities = true
    },

    async showFarmActivities (company, farm) {
      this.selectedFarm = farm
      this.selectedClient = company
      this.showFarmMonthlyActivities = true
    },

    closeMonthlyActivities () {
      this.showMonthlyActivities = false
      setTimeout(() => {
        this.selectedClient = null
      }, 300)
    },

    closeMonthlyFarmActivities () {
      this.showFarmMonthlyActivities = false
      setTimeout(() => {
        this.selectedFarm = null
        this.selectedClient = null
      }, 300)
    },

    hasComments (item) {
      return Boolean(item.comment)
    },

    async showComment (item, farm = null) {
      if (!this.isAdmin && !this.isStaff) {
        this.$toasted.show(this.$t('activities.no_access'))
        return
      }
      this.selectedFarm = farm

      if (!farm) {
        this.selectedFarm = null
      }
      this.selectedCommentClient = item
      this.commentModal.show = true
    },

    closeCommentModal () {
      this.commentModal.show = false
      setTimeout(() => {
        this.selectedCommentClient = null
        this.existingCommentForEdit = null
      }, 300)
    },

    openAddCommentForm (item, farm = null) {
      if (!this.isAdmin && !this.isStaff) {
        this.$toasted.show(this.$t('activities.no_access'))
        return
      }
      this.selectedCommentClient = item
      this.selectedFarm = farm

      this.existingCommentForEdit = null
      this.commentFormModal.show = true
    },

    closeCommentFormModal () {
      this.commentFormModal.show = false
      setTimeout(() => {
        this.selectedCommentClient = null
        this.selectedFarm = null
        this.existingCommentForEdit = null
      }, 300)
    },

    handleAddCommentFromModal (data) {
      this.commentModal.show = false
      this.commentFormModal.show = true

      if (data && data.existingComment) {
        this.existingCommentForEdit = data.existingComment
      } else {
        this.existingCommentForEdit = null
        this.selectedCommentClient = null
        this.selectedFarm = null
      }
    },

    async handleCreateComment (data) {
      try {
        this.$toasted.show('Коментар успішно додано')
        if (!data || !data.clientId) {
          console.error('Invalid data received:', data)
          return
        }

        const clientId = data.clientId
        const farmId = data.farmId

        if (farmId) {
          const companyIndex = this.items.findIndex(c => c.id === clientId)
          if (companyIndex !== -1 && this.items[companyIndex].farms) {
            const farmIndex = this.items[companyIndex].farms.findIndex(f => f.id === farmId)
            if (farmIndex !== -1) {
              this.$set(this.items[companyIndex].farms[farmIndex], 'comment', true)
            }
          }
        } else {
          const companyIndex = this.items.findIndex(c => c.id === clientId)
          if (companyIndex !== -1) {
            this.$set(this.items[companyIndex], 'comment', true)
          }
        }

        this.closeCommentFormModal()
      } catch (error) {
        console.error('Failed to handle comment creation:', error)
        this.$toasted.error('Помилка при оновленні даних')
        this.closeCommentFormModal()
      }
    },
    async handleCommentDeleted (data) {
      try {
        if (!data || !data.clientId) {
          console.error('Invalid data received:', data)
          return
        }
        const clientId = data.clientId
        const farmId = data.farmId
        const wasExpanded = this.expandedCompanies.includes(clientId)

        if (farmId && wasExpanded) {
          try {
            const expandedIndex = this.expandedCompanies.indexOf(clientId)
            if (expandedIndex > -1) {
              this.expandedCompanies.splice(expandedIndex, 1)
            }

            const companyIndex = this.items.findIndex(c => c.id === clientId)
            if (companyIndex !== -1) {
              this.$set(this.items[companyIndex], 'farms', [])
            }

            this.$delete(this.farmsPagination, clientId)
            this.$delete(this.farmsSearch, clientId)
            this.$delete(this.farmsSearchTimeout, clientId)
            await this.loadData()
            await this.$nextTick()
            await this.toggleCompanyExpansion(clientId)
          } catch (error) {
            console.error('Error updating farms after comment deletion:', error)
            await this.loadData()
          }
        } else {
          await this.loadData()
          if (wasExpanded) {
            try {
              if (this.farmsPagination[clientId]) {
                this.$set(this.farmsPagination[clientId], 'offset', 0)
                this.$set(this.farmsPagination[clientId], 'hasMore', true)
              }
              await this.loadCompanyFarms(clientId, false)
            } catch (farmError) {
              console.error('Failed to reload farms after company comment deletion:', farmError)
            }
          }
        }
        this.closeCommentModal()
      } catch (error) {
        console.error('Failed to handle comment deletion:', error)
        this.$toasted.error('Помилка при оновленні даних після видалення')
      }
    },
    onCompanyClick (company) {
      const KEY = `company_${company.id}_name`
      sessionStorage.setItem(KEY, company.display_name || company.name)
      this.$router.push(`${this.$route.path}/${company.id}/farms`)
    },



    async loadCompanyFarms (companyId, loadMore = false) {
      try {
        if (!this.farmsPagination[companyId]) {
          this.$set(this.farmsPagination, companyId, {
            offset: 0,
            hasMore: true,
            totalCount: 0,
            loading: false
          })
        }

        const pagination = this.farmsPagination[companyId]
        const searchQuery = this.farmsSearch[companyId]
        this.$set(this.farmsPagination[companyId], 'loading', true)

        const params = {
          offset: loadMore ? pagination.offset : 0,
          limit: 100
        }

        if (searchQuery && searchQuery.trim()) {
          params.name = searchQuery.trim()
        }
        const response = await api.dashboard.groups.getClientFarmView(companyId, params)
        let data, count, error
        if (response && typeof response === 'object') {
          if (response.data !== undefined) {
            data = response.data
            count = response.count || (Array.isArray(data) ? data.length : 0)
            error = response.error
          } else if (Array.isArray(response)) {
            data = response
            count = response.length
            error = null
          } else {
            data = response.results || response.items || []
            count = response.total || response.count || data.length
            error = response.error
          }
        } else {
          console.error('Invalid response format:', response)
          throw new Error('Invalid API response format')
        }

        if (error) {
          console.error('API returned error:', error)
          throw new Error(`Failed to load farms: ${error}`)
        }

        if (!Array.isArray(data)) {
          console.error('Data is not an array:', data)
          data = []
        }

        const companyIndex = this.items.findIndex(c => c.id === companyId)

        if (companyIndex !== -1) {
          const transformedFarms = data.map(farm => ({
            ...farm,
            rowType: this.getRowType(farm.latest_status, farm.is_active)
          }))

          if (loadMore && this.items[companyIndex].farms && this.items[companyIndex].farms.length > 0) {
            this.items[companyIndex].farms.push(...transformedFarms)
          } else {
            this.$set(this.items[companyIndex], 'farms', transformedFarms)
          }

          const newOffset = (loadMore ? pagination.offset : 0) + data.length
          this.$set(this.farmsPagination[companyId], 'offset', newOffset)
          this.$set(this.farmsPagination[companyId], 'totalCount', count)
          this.$set(this.farmsPagination[companyId], 'hasMore', newOffset < count)
          this.$forceUpdate()
        } else {
          console.error('Company not found in items array')
        }
      } catch (error) {
        console.error('=== loadCompanyFarms failed ===', error)
        throw error
      } finally {
        if (this.farmsPagination[companyId]) {
          this.$set(this.farmsPagination[companyId], 'loading', false)
        }
      }
    },
    onFarmsSearchInput (companyId, searchValue) {
      if (this.farmsSearchTimeout[companyId]) {
        clearTimeout(this.farmsSearchTimeout[companyId])
      }

      this.$set(this.farmsSearch, companyId, searchValue)

      this.$set(this.farmsSearchTimeout, companyId, setTimeout(() => {
        this.searchFarms(companyId)
      }, 500))
    },

    async searchFarms (companyId) {
      try {
        this.$set(this.farmsPagination[companyId], 'offset', 0)
        this.$set(this.farmsPagination[companyId], 'hasMore', true)

        await this.loadCompanyFarms(companyId, false)
      } catch (error) {
        console.error('Failed to search farms:', error)
      }
    },

    clearFarmsSearch (companyId) {
      this.$set(this.farmsSearch, companyId, '')
      this.searchFarms(companyId)
    },

    async loadMoreFarms (companyId) {
      await this.loadCompanyFarms(companyId, true)
    },

    async toggleCompanyExpansion (companyId) {
      const index = this.expandedCompanies.indexOf(companyId)
      if (index > -1) {
        this.expandedCompanies.splice(index, 1)
        if (this.farmsPagination[companyId]) {
          this.$set(this.farmsPagination[companyId], 'offset', 0)
          this.$set(this.farmsPagination[companyId], 'hasMore', true)
        }
      } else {
        this.items.find(c => c.id === companyId)

        this.$set(this.loadingFarms, companyId, true)

        try {
          await this.loadCompanyFarms(companyId)
          this.expandedCompanies.push(companyId)
        } catch (error) {
          console.error('Failed to expand company:', error)
        } finally {
          this.$set(this.loadingFarms, companyId, false)
        }
      }
    },

    onFarmClick (company, farm) {
      const KEY = `company_${company.id}_name`
      sessionStorage.setItem(KEY, company.display_name || company.name)
      const FARM_KEY = `farm_${farm.id}_name`
      sessionStorage.setItem(FARM_KEY, farm.name)
      this.$router.push(`${this.$route.path}/${company.id}/farms/${farm.id}`)
    },

    getFarmClass (farm) {
      return {
        'row-warning': farm.rowType === 'warning',
        'row-error': farm.rowType === 'error',
        'row-success': farm.rowType === 'success',
        'row-default': farm.rowType === 'default',
      }
    },

    getRowClass (item) {
      return {
        'row-warning': item.rowType === 'warning',
        'row-error': item.rowType === 'error',
        'row-success': item.rowType === 'success',
        'row-default': item.rowType === 'default',
      }
    },

    getRatioClass (done, planned) {
      if (done === 0) {
        return 'ratio-error'
      }
      if (done >= planned) {
        return 'ratio-success'
      }
      return 'ratio-warning'
    },

    editCompany (company) {
      this.selectedClient = company
      this.modals.showSettingsClient = true
      this.closeAllDropdowns()
    },

    changePassword (company) {
      this.selectedClient = company
      this.modals.showChangePasswordClient = true
      this.closeAllDropdowns()
    },

    closeAllDropdowns () {
      this.$children.forEach(child => {
        if (child.$refs && child.$refs.dropdown) {
          child.$refs.dropdown.closeDropdown()
        }
      })

      const dropdowns = document.querySelectorAll('.v-dropdown')
      dropdowns.forEach(dropdown => {
        const dropdownInstance = dropdown.__vue__
        if (dropdownInstance && dropdownInstance.closeDropdown) {
          dropdownInstance.closeDropdown()
        }
      })
    },

    onSearchInput () {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        this.page = 1
        this.loadData()
      }, 500)
    },

    sortBy (field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDirection = 'asc'
      }

      this.page = 1
      this.loadData()
    }
  }
}
</script>

<style lang="scss" scoped>
// Skeleton Loader Styles
.skeleton-row {
  pointer-events: none;
}

.skeleton-header {
  width: 80px;
  height: 16px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.skeleton-company-name {
  width: 160px;
  height: 18px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.skeleton-status {
  width: 80px;
  height: 16px;
  background-color: #f5f5f5;
  border-radius: 4px;
}
.skeleton-count-inline {
  width: 40px;
  height: 16px;
  background-color: #b2b2b2;
  border-radius: 4px;
  display: inline-block;
}
.skeleton-count {
  width: 40px;
  height: 16px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.skeleton-badge {
  width: 50px;
  height: 24px;
  background-color: #f5f5f5;
  border-radius: 12px;
}

.skeleton-ratio {
  width: 60px;
  height: 16px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.skeleton-icon {
  width: 24px;
  height: 24px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.skeleton-menu {
  width: 32px;
  height: 32px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.skeleton-shimmer {
  position: relative;
  overflow: hidden;
}

.skeleton-shimmer::after {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  animation: shimmer 1.5s infinite;
  content: '';
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>